import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const APP_CLIENT_ID = import.meta.env.VITE_COGNITO_APP_CLIENT_ID;

let userPool: CognitoUserPool | null = null;

export function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!USER_POOL_ID || !APP_CLIENT_ID) {
      throw new Error(
        'Cognito User Pool configuration missing. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_APP_CLIENT_ID.'
      );
    }
    userPool = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: APP_CLIENT_ID,
    });
  }
  return userPool;
}

export interface SignInResult {
  session?: CognitoUserSession;
  /** Set when Cognito requires a new password (first login with temp password) */
  newPasswordRequired?: {
    userAttributes: Record<string, string>;
    cognitoUser: CognitoUser;
  };
}

export function signIn(
  username: string,
  password: string
): Promise<SignInResult> {
  const pool = getUserPool();
  const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ session }),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // Remove read-only/immutable attributes that Cognito returns but won't accept back
        const readOnly = [
          'email_verified',
          'phone_number_verified',
          'email',
          'phone_number',
          'sub',
        ];
        for (const attr of readOnly) {
          delete userAttributes[attr];
        }
        resolve({ newPasswordRequired: { userAttributes, cognitoUser } });
      },
    });
  });
}

export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  const pool = getUserPool();
  const currentUser = pool.getCurrentUser();
  if (currentUser) {
    currentUser.signOut();
  }
}

export function getCurrentSession(): Promise<CognitoUserSession | null> {
  try {
    const pool = getUserPool();
    const currentUser = pool.getCurrentUser();
    if (!currentUser) return Promise.resolve(null);

    return new Promise((resolve) => {
      currentUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            resolve(null);
          } else {
            resolve(session);
          }
        }
      );
    });
  } catch {
    return Promise.resolve(null);
  }
}

export function getCurrentUser(): CognitoUser | null {
  try {
    return getUserPool().getCurrentUser();
  } catch {
    return null;
  }
}
